/**
 * Episode JSON Import Component
 *
 * Story 11.19: Episode Export/Import JSON
 * Reusable component for importing episode data from JSON files.
 *
 * Can be used for:
 * - Importing full episode data on Create Episode page
 * - Importing display localizations on Localization page
 */

import { useRef } from 'react';
import toast from 'react-hot-toast';
import {
  type EpisodeExport,
  validateEpisodeImport,
  validateLocalizationsImport,
} from '../utils/episodeExportImport';

// ============================================================================
// Types
// ============================================================================

interface EpisodeJsonImportProps {
  /** Type of import: 'episode' for Create page, 'localizations' for Localization page */
  importType: 'episode' | 'localizations';
  /** Called with validated episode data when importType is 'episode' */
  onEpisodeImport?: (data: EpisodeExport) => void;
  /** Called with validated localizations when importType is 'localizations' */
  onLocalizationsImport?: (
    localizations: Array<{
      language_code: string;
      localized_name: string;
      localized_context: string;
      localized_goals: string[];
      localized_personas: Array<{ name: string; description: string }>;
    }>
  ) => void;
  /** Button label - defaults based on importType */
  buttonLabel?: string;
  /** Optional CSS class for the button */
  className?: string;
  /** Whether the button is disabled */
  disabled?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function EpisodeJsonImport({
  importType,
  onEpisodeImport,
  onLocalizationsImport,
  buttonLabel,
  className = '',
  disabled = false,
}: EpisodeJsonImportProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultLabel =
    importType === 'episode' ? 'Import from JSON' : 'Import Localizations from JSON';

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be selected again
    event.target.value = '';

    try {
      const text = await file.text();
      let data: unknown;

      try {
        data = JSON.parse(text);
      } catch {
        toast.error('Invalid JSON file. Please check the file format.');
        return;
      }

      if (importType === 'episode') {
        const result = validateEpisodeImport(data);
        if (!result.valid) {
          toast.error(result.error || 'Invalid episode data');
          return;
        }
        onEpisodeImport?.(result.data!);
        toast.success('Episode data imported. Review and save.');
      } else {
        const result = validateLocalizationsImport(data);
        if (!result.valid) {
          toast.error(result.error || 'Invalid localization data');
          return;
        }
        onLocalizationsImport?.(result.localizations!);
      }
    } catch {
      toast.error('Failed to read file. Please try again.');
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".json,application/json"
        className="hidden"
        data-testid="json-file-input"
      />
      <button
        type="button"
        onClick={handleButtonClick}
        disabled={disabled}
        className={`inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        data-testid="import-json-button"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
          />
        </svg>
        {buttonLabel || defaultLabel}
      </button>
    </>
  );
}
